import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Upload, Pencil, Boxes, ChefHat, FileSpreadsheet, Factory } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { MonthPicker, labelForMonth, useSharedMonth } from '../components/MonthPicker';
import { api, fmtDate, fmtMoney, fmtQty } from '../lib/api';
import { PriceDeltaBadge } from '../components/PriceDeltaBadge';
import { PetpoojaSyncTab } from './stock/PetpoojaSyncTab';
import { RecipesTab } from './stock/RecipesTab';
import { StockTab } from './stock/StockTab';
import { ProductionTab } from './stock/ProductionTab';

// 2026-07: Merge UX removed. Users now rename items directly. If two items
// under the same vendor end up with the same name after rename, they
// automatically aggregate as one (the backend re-normalizes and the /items
// endpoint groups by normalized_name).
//
// Sprint 2026-08 (Phase 7): the page is now a tabbed container.
//   - Items           → the original raw-material list (aggregated from bills)
//   - Recipes         → menu items × Bill of Materials (Petpooja bridge)
//   - Stock           → live stock levels + ledger
//   - Petpooja Sync   → daily CSV upload

export default function ItemsPage() {
    // Persist the last-open tab across refreshes so returning to the page
    // doesn't kick the user back to "Items" after every reload.
    const [tab, setTab] = useState(() => {
        try {
            return localStorage.getItem('emb.items.tab') || 'items';
        } catch {
            return 'items';
        }
    });
    const changeTab = (v) => {
        setTab(v);
        try { localStorage.setItem('emb.items.tab', v); } catch { /* noop */ }
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <Tabs value={tab} onValueChange={changeTab} className="w-full">
                <TabsList className="grid grid-cols-5 max-w-3xl">
                    <TabsTrigger value="items" data-testid="items-tab-items">
                        <Package size={14} className="mr-1.5" /> Items
                    </TabsTrigger>
                    <TabsTrigger value="recipes" data-testid="items-tab-recipes">
                        <ChefHat size={14} className="mr-1.5" /> Recipes
                    </TabsTrigger>
                    <TabsTrigger value="stock" data-testid="items-tab-stock">
                        <Boxes size={14} className="mr-1.5" /> Stock
                    </TabsTrigger>
                    <TabsTrigger value="production" data-testid="items-tab-production">
                        <Factory size={14} className="mr-1.5" /> Production
                    </TabsTrigger>
                    <TabsTrigger value="petpooja" data-testid="items-tab-petpooja">
                        <FileSpreadsheet size={14} className="mr-1.5" /> Petpooja
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="items" className="mt-4">
                    <ItemsTable />
                </TabsContent>
                <TabsContent value="recipes" className="mt-4">
                    <RecipesTab />
                </TabsContent>
                <TabsContent value="stock" className="mt-4">
                    <StockTab />
                </TabsContent>
                <TabsContent value="production" className="mt-4">
                    <ProductionTab />
                </TabsContent>
                <TabsContent value="petpooja" className="mt-4">
                    <PetpoojaSyncTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}


function ItemsTable() {
    const [rows, setRows] = useState(null);
    const [month, setMonth] = useSharedMonth();
    const [sortBy, setSortBy] = useState('latest');
    const [sortDir, setSortDir] = useState('desc');
    const [renameTarget, setRenameTarget] = useState(null); // row object being renamed
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);

    const reload = useCallback(async () => {
        setRows(null);
        const params = { sort_by: sortBy, sort_dir: sortDir };
        if (month && month !== 'all') params.month = month;
        try {
            const r = await api.listItems(params);
            setRows(r || []);
        } catch {
            setRows([]);
        }
    }, [sortBy, sortDir, month]);

    useEffect(() => { reload(); }, [reload]);

    const totalQty = useMemo(
        () => (rows || []).reduce((s, r) => s + (Number(r.total_quantity) || 0), 0),
        [rows],
    );
    const totalSpend = useMemo(
        () => (rows || []).reduce((s, r) => s + (Number(r.total_amount) || 0), 0),
        [rows],
    );

    function openRename(row) {
        setRenameTarget(row);
        setRenameValue(row.item_name || '');
    }

    async function submitRename() {
        if (!renameTarget) return;
        const newName = renameValue.trim();
        if (!newName) {
            toast.error('Please enter a name.');
            return;
        }
        if (newName === (renameTarget.item_name || '').trim()) {
            setRenameTarget(null);
            return;
        }
        setRenaming(true);
        try {
            const r = await api.renameItem({
                vendor_key: renameTarget.vendor_key,
                item_key: renameTarget.item_key,
                new_name: newName,
            });
            if (r.merged_with_existing) {
                toast.success(
                    `Renamed to "${newName}" — this item was combined with an existing one under ${renameTarget.vendor}.`,
                    { duration: 5000 },
                );
            } else {
                toast.success(`Renamed to "${newName}" across ${r.bills_updated || 0} bill${r.bills_updated === 1 ? '' : 's'}.`);
            }
            setRenameTarget(null);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Rename failed.');
        } finally {
            setRenaming(false);
        }
    }

    return (
        <div className="space-y-5 animate-fade-in">
            <PageHeader
                title="Items"
                subtitle={
                    month && month !== 'all'
                        ? `Items appearing in bills from ${labelForMonth(month)}, grouped by vendor.`
                        : 'Every unique item across your bills, grouped by vendor. See latest price, range, and open the full price history.'
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                        <Select value={`${sortBy}:${sortDir}`} onValueChange={(v) => {
                            const [b, d] = v.split(':'); setSortBy(b); setSortDir(d);
                        }}>
                            <SelectTrigger data-testid="items-sort" className="min-w-[160px] flex-1 sm:flex-none sm:min-w-[180px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="latest:desc">Latest seen (newest)</SelectItem>
                                <SelectItem value="latest:asc">Latest seen (oldest)</SelectItem>
                                <SelectItem value="quantity:desc">Qty (most first)</SelectItem>
                                <SelectItem value="quantity:asc">Qty (least first)</SelectItem>
                                <SelectItem value="amount:desc">Spend (₹ high → low)</SelectItem>
                                <SelectItem value="amount:asc">Spend (₹ low → high)</SelectItem>
                                <SelectItem value="bills:desc">Bill count (high → low)</SelectItem>
                                <SelectItem value="price:desc">Latest price (high → low)</SelectItem>
                                <SelectItem value="price:asc">Latest price (low → high)</SelectItem>
                                <SelectItem value="name:asc">Item name (A → Z)</SelectItem>
                                <SelectItem value="vendor:asc">Vendor (A → Z)</SelectItem>
                            </SelectContent>
                        </Select>
                        <MonthPicker value={month} onChange={setMonth} testid="items-month-picker" />
                        <Link to="/upload" className="ml-auto sm:ml-0">
                            <Button className="gap-2">
                                <Upload size={16} /> Upload Bill
                            </Button>
                        </Link>
                    </div>
                }
            />
            <Card className="p-0 border-border/80 overflow-hidden">
                {rows === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Package size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No items yet</div>
                        <div className="text-sm text-muted-foreground">Upload a bill to see extracted items here.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="items-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Item</th>
                                    <th className="text-left px-4 py-2 font-medium">Vendor</th>
                                    <th className="text-left px-4 py-2 font-medium">Last seen</th>
                                    <th className="text-right px-4 py-2 font-medium">Bills</th>
                                    <th className="text-right px-4 py-2 font-medium">Total Qty</th>
                                    <th className="text-right px-4 py-2 font-medium">Spent</th>
                                    <th className="text-right px-4 py-2 font-medium">Latest price<span className="block text-[10px] font-normal text-muted-foreground normal-case">incl. GST</span></th>
                                    <th className="text-right px-4 py-2 font-medium">Range</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, idx) => {
                                    const delta = r.max_price != null && r.min_price != null ? (r.max_price - r.min_price) : 0;
                                    const pct = r.min_price ? ((r.max_price - r.min_price) / r.min_price) * 100 : 0;
                                    const isMeaningfulDelta = delta >= 1.0 || pct >= 1.0;
                                    const qty = Number(r.total_quantity || 0);
                                    const qtyDisplay = fmtQty(qty);
                                    return (
                                        <tr key={`${r.vendor_key}::${r.item_key}`} className="border-t border-border hover:bg-muted/40">
                                            <td className="px-4 py-2">
                                                <Link
                                                    to={`/items/detail?vendor_key=${encodeURIComponent(r.vendor_key || '')}&item_key=${encodeURIComponent(r.item_key || '')}`}
                                                    className="hover:underline"
                                                    data-testid={`items-row-${idx}`}
                                                >
                                                    {r.item_name}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2 text-muted-foreground uppercase">{r.vendor}</td>
                                            <td className="px-4 py-2 text-muted-foreground">{fmtDate(r.latest_bill_date)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono">{r.bill_count}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono" data-testid={`items-qty-${idx}`}>{qtyDisplay}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(r.total_amount)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(r.latest_price)}</td>
                                            <td className="px-4 py-2 text-right">
                                                {delta > 0 && isMeaningfulDelta ? <PriceDeltaBadge delta={delta} deltaPercent={pct} showIcon={false} /> : <span className="text-muted-foreground text-xs">flat</span>}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="gap-1 h-7"
                                                    onClick={() => openRename(r)}
                                                    data-testid={`items-rename-${idx}`}
                                                    title="Rename this item across all bills"
                                                >
                                                    <Pencil size={13} /> Rename
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {rows.length > 0 && (
                                <tfoot className="bg-muted/20 border-t-2 border-border font-medium">
                                    <tr>
                                        <td className="px-4 py-2 text-xs uppercase text-muted-foreground" colSpan={3}>
                                            Total ({rows.length} item{rows.length !== 1 ? 's' : ''}
                                            {month && month !== 'all' ? ` in ${labelForMonth(month)}` : ''})
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">—</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono" data-testid="items-total-qty">{fmtQty(totalQty)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono" data-testid="items-total-spend">{fmtMoney(totalSpend)}</td>
                                        <td className="px-4 py-2" colSpan={3}></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </Card>

            {/* Rename dialog */}
            <Dialog
                open={!!renameTarget}
                onOpenChange={(v) => { if (!v && !renaming) setRenameTarget(null); }}
            >
                <DialogContent data-testid="item-rename-dialog">
                    <DialogHeader>
                        <DialogTitle>Rename item</DialogTitle>
                        <DialogDescription>
                            The new name will be applied to every bill from{' '}
                            <span className="font-medium uppercase">{renameTarget?.vendor}</span>{' '}
                            that has this item. If another item with the same name already exists
                            under this vendor, they will be combined into one.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs mb-1.5 block text-muted-foreground">Current name</Label>
                            <div className="text-sm px-3 py-2 rounded-md border border-border/60 bg-muted/40 text-muted-foreground">
                                {renameTarget?.item_name || '—'}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="item-rename-input" className="text-xs mb-1.5 block">
                                New name
                            </Label>
                            <Input
                                id="item-rename-input"
                                data-testid="item-rename-input"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
                                maxLength={200}
                                autoFocus
                                placeholder="e.g. Coca Cola 500ml"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRenameTarget(null)}
                            disabled={renaming}
                            data-testid="item-rename-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitRename}
                            disabled={renaming || !renameValue.trim()}
                            data-testid="item-rename-submit"
                        >
                            {renaming ? 'Renaming…' : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
